import React, { useEffect, useState } from 'react';
import PlaybookHero from '../../../components/playbooks/PlaybookHero';
import { InformationCircleIcon, AcademicCapIcon, PlayCircleIcon, WrenchScrewdriverIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import VideoEmbed from '../../../components/playbooks/VideoEmbed';
import PlaybookMetrics from '../../../components/playbooks/PlaybookMetrics';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';
import { getResourceBySlug } from '../../../lib/db';

const WorkingThroughConflictKeyInfo = () => {
  const [resource, setResource] = useState(null);
  const metrics = [
    {
      type: 'difficulty',
      title: 'Difficulty',
      value: 'Egos involved so... extra hard.'
    },
    {
      type: 'timeline',
      title: 'Timeline',
      value: '2 Hours - several months depending on the type of conflict'
    },
    {
      type: 'size',
      title: 'Team Size',
      value: 'All'
    },
    {
      type: 'why',
      title: 'Why Use',
      value: 'At the macro, conflict (the destructive kind) kills execution and is bad for business. At the micro it damages your mental health. We don\'t want any of that.'
    },
    {
      type: 'when',
      title: 'When to Use',
      value: 'If you notice yourself in personal conflict with someone else, if your leadership team is failing to have healthy conflict, or if you observe two teams/functions failing to collaborate effectively.'
    }
  ];

  useEffect(() => {
    getResourceBySlug('working-through-conflict').then(setResource);
  }, []);

  return (
    <div>
      <PlaybookHero
        title={resource?.title || "Working Through Conflict"}
        subtitle={resource?.description || "3 Scenario Based Toolkits"}
        category={resource?.category || "Culture Playbook"}
        bannerUrl={resource?.banner_url}
        readingTime={resource?.timeframe || "15 min read"}
        type={resource?.type}
      />
      <div id="key-info" className="prose prose-invert max-w-none space-y-8">
        {/* This Playbook is for you section */}
        <div className="border-l-4 border-primary-light p-6 bg-gray-900/50 italic text-gray-300">
          <p className="font-semibold mb-2">This Playbook is for you if any of the following (or similar) are true:</p>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>You have a personal disagreement with the founder or another member of the leadership team about a flammable decision e.g. remote vs hybrid, compensation philosophy, benefits change, a potential hire, a potential fire – anything!</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>You can see the leadership team as a whole failing to work through a conflict in a healthy way #avoidance #blame</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span>You can see two (or more) teams/functions not working well together.</span>
            </li>
          </ul>
        </div>

        {/* Key Info section */}
        <div>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <InformationCircleIcon className="h-6 w-6 text-primary-light mr-3" />
            Key Info
          </h2>
          {/* Metrics */}
          <div className="mb-8">
            <PlaybookMetrics metrics={metrics} />
          </div>
        </div>

        {/* Watch This section */}
        <div className="p-6 border border-gray-800 rounded-lg bg-gray-900/50">
          <div className="flex items-center mb-4">
            <PlayCircleIcon className="h-7 w-7 text-primary-light mr-3" />
            <h3 className="text-xl font-semibold">Watch This</h3>
          </div>
          <VideoEmbed 
            src="https://www.loom.com/embed/ccdf63a105f14ecdae8790da2e3c67c5" 
            title="Working Through Conflict Introduction" 
          />
          <div className="mt-4">
           
          </div>
        </div>

        {/* What Is This Content */}
        <div className="p-6 border border-gray-800 rounded-lg bg-gray-900/50">
          <div className="flex items-center mb-4">
            <AcademicCapIcon className="h-7 w-7 text-primary-light mr-3" />
            <h3 className="text-xl font-semibold">What Is This</h3>
          </div>
          <div className="space-y-4">
            <div className="italic text-gray-300 text-lg p-4 border-l-4 border-primary-light rounded-r-lg bg-gray-900/30 mb-4">
              <p className="mb-2">
                "It is wise to direct your anger towards problems - not people; to focus your energies on answers - not excuses."
              </p>
              <p className="text-gray-400">— William Arthur Ward</p>
            </div>
            <p className="text-gray-300">
              Welcome to your step-by-step guide on working through conflict. We've spoken to hundreds of people leaders and founders over the years and identified some common kinds of conflict that you might come across. We've created a pragmatic and practical mini toolkit for each (see tiles below).
            </p>
            <p className="text-gray-300">
              We need to do this, but this is hard:
            </p>
            <div className="my-4">
              <PlaybookImage 
                src="https://auth.openorg.fyi/storage/v1/object/public/resources/working-conflict/healthy-unhealthy-conflict.png" 
                alt="Healthy vs Unhealthy Conflict Diagram" 
                className="rounded-lg border border-gray-700/50"
              />
            </div>
            <p className="text-gray-300 font-semibold">
              Let's get into it 🤘
            </p>
          </div>
        </div>

        {/* What You'll Need Content */}
        <div className="p-6 border border-gray-800 rounded-lg bg-gray-900/50">
          <div className="flex items-center mb-4">
            <WrenchScrewdriverIcon className="h-7 w-7 text-primary-light mr-3" />
            <h3 className="text-xl font-semibold">What You'll Need</h3>
          </div>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span className="text-gray-300">The heart of a lion and the patience of a saint</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default WorkingThroughConflictKeyInfo; 